<template>
  <div class="result-container" :class="{ 'result-container-mobile': isMobile }">
    <div v-if="isMobile" class="searchbar-top">
      <search-bar :flex="true"></search-bar>
    </div>
    <div class="tab-container" :class="{ 'tab-container-singleline': isMobile }">
      <module-tabbar v-model:activeTab="activeTab" :modules="modules" :singleLine="true"></module-tabbar>
      <div v-if="activeTab !== 'all'" class="sort-container">
        <div>{{ capitalize($t('sort by')) }}</div>
        <div>
          <custom-select v-model="sortField" :options="sortFieldOptions"></custom-select>
        </div>
        <div>
          <custom-select v-model="sortOrder" :options="sortOrderOptions"></custom-select>
        </div>
      </div>
      <div v-if="activeTab !== 'all' && !isMobile" class="listmode-container">
        <div class="choice-container">
          <div
            class="choice-option"
            :class="{ 'choice-option-active': resultListMode === 'list' }"
            :title="capitalize($t('resultlistmode.label')) + ': ' + $t('resultlistmode.list')"
            @click="setResultListMode('list')"
          >
            <img src="/img/icons/catalogue_mode_list.png" alt="list">
          </div>
          <div
            class="choice-option"
            :class="{ 'choice-option-active': resultListMode === 'tiles' }"
            :title="capitalize($t('resultlistmode.label')) + ': ' + $t('resultlistmode.tiles')"
            @click="setResultListMode('tiles')"
          >
            <img src="/img/icons/catalogue_mode_tiles.png" alt="tiles">
          </div>
        </div>
      </div>
    </div>
    <div class="tabcontent-container">
        <module-aggregate-view
          v-if="activeTab === 'all'"
          :modules="modules"
          :mobile="isMobile"
          @select="onSelectPublication"
          @navigateToTab="setActiveTab"
        ></module-aggregate-view>
        <module-result-view
          ref="moduleResultView"
          v-if="activeTab !== 'all'"
          :connectionId="activeTab"
          :selectedPublication="selectedPublication"
          :mode="isMobile ? 'list' : resultListMode"
          :mobile="isMobile"
          @select="onSelectPublication"
        ></module-result-view>
        <Transition name="paneltransition">
          <module-result-detail
            v-show="detailPanelOpen"
            ref="detailLoader"
            :actionAddToBookshelf="true"
            @close="closeDetailPanel"
          ></module-result-detail>
        </Transition>
    </div>
  </div>
</template>

<script>
import ModuleTabbar from '@/components/module-tabbar/ModuleTabbar'
import ModuleAggregateView from '@/components/module-aggregate/ModuleAggregateView'
import ModuleResultView from '@/components/module-results/ModuleResultView'
import CustomSelect from '@/components/form-elements/CustomSelect'
import ModuleResultDetail from '@/components/module-results/ModuleResultDetail'
import SearchBar from '@/components/search/SearchBar'
import { mapState } from 'vuex'
import { Events } from '@/events'
import queryObjectValidator from '@/utilities/queryObjectValidator'
import ScreenSizeMixin from '@/mixins/screensize'

export default {
  name: 'results-view',
  mixins: [ScreenSizeMixin],
  computed: {
    ...mapState('searchStore/searchModules', {
      modules: state => state.modules
    }),
    ...mapState('searchStore', {
      sortFieldOptions: state => state.config.sortOptions.sortFieldOptions,
      sortOrderOptions: state => state.config.sortOptions.sortOrderOptions
    }),
    sortField: {
      get () { return this.$store.state.searchStore.lastSearchParams?.sortField },
      set (value) {
        if (value === this.sortField) return

        this.$posthog.capture('Sort', {
          sortField: value,
          sortOrder: this.sortOrder
        })

        this.$store.dispatch('searchStore/changeLastSearchSorting', {
          sortField: value,
          sortOrder: this.sortOrder
        })
      }
    },
    sortOrder: {
      get () { return this.$store.state.searchStore.lastSearchParams?.sortOrder },
      set (value) {
        if (value === this.sortOrder) return

        this.$posthog.capture('Sort', {
          sortField: this.sortField,
          sortOrder: value
        })

        this.$store.dispatch('searchStore/changeLastSearchSorting', {
          sortField: this.sortField,
          sortOrder: value
        })
      }
    },
    activeTab: {
      get () { return this.$store.state.searchStore.activeResultTab },
      set (value) {
        if (value === this.activeTab) return
        if (this.pendingTabChange) return
        const moduleResultView = this.$refs.moduleResultView
        if (moduleResultView && moduleResultView.checkedResults && moduleResultView.checkedResults.length > 0) {
          this.pendingTabChange = value
          this.confirmBeforeLeavingWithResults((confirmed) => {
            if (confirmed) {
              this.$store.commit('searchStore/setActiveResultTab', value)
            }
            this.pendingTabChange = null
          })
        } else {
          this.$store.commit('searchStore/setActiveResultTab', value)
        }
      }
    }
  },
  data: () => ({
    selectedPublication: null,
    detailPanelOpen: false,
    resultListMode: 'list',
    pendingTabChange: null
  }),
  methods: {
    onSelectPublication (publicationInfo) {
      if (!this.$refs.detailLoader?.loadPublication) return

      this.$refs.detailLoader.loadPublication(publicationInfo)
      this.detailPanelOpen = true
      this.selectedPublication = publicationInfo.publication
    },
    closeDetailPanel () {
      this.detailPanelOpen = false
      this.selectedPublication = null
    },
    setActiveTab (tab) {
      if (this.activeTab !== tab) this.closeDetailPanel()
      this.activeTab = tab
    },
    onNewSearch () {
      this.closeDetailPanel()
      const targetTab = (this.modules.length === 1) ? this.modules[0].connection : 'all'
      this.setActiveTab(targetTab)
    },
    onBeforeSearchStarted ({ confirm, cancel }) {
      const moduleResultView = this.$refs.moduleResultView
      const hasCheckedResults = !!(moduleResultView && moduleResultView.checkedResults && moduleResultView.checkedResults.length > 0)

      if (!hasCheckedResults) {
        if (confirm) confirm()
        return
      }

      this.confirmBeforeLeavingWithResults((confirmed) => {
        if (confirmed) {
          Events.$emit('uncheckResults')
          if (confirm) confirm()
        } else {
          if (cancel) cancel()
        }
      })
    },
    async launchSearchFromUrl () {
      const queryJsonString = this.$route.query?.query
      let queryJson = null
      try {
        if (queryJsonString) { queryJson = JSON.parse(queryJsonString) }
      } catch (e) {
        return this.onSetQueryFromUrlError(e.message)
      }
      if (!queryJson) {
        return this.onSetQueryFromUrlError('Query urlparam empty')
      }

      const validationResult = queryObjectValidator.validate(queryJson)
      if (validationResult.error) {
        return this.onSetQueryFromUrlError(validationResult.error)
      }

      await this.$store.dispatch('searchStore/searchFromUrlQueryObject', validationResult.value)
      this.$router.push({ name: 'results' })
    },
    onSetQueryFromUrlError (errorMessage) {
      console.warn(errorMessage)
    },
    setResultListMode (mode) {
      if (mode === this.resultListMode) return

      this.resultListMode = mode
      this.$posthog.capture('ResultListModeChanged', {
        mode
      })
    },
    confirmBeforeLeavingWithResults (callback) {
      const moduleResultView = this.$refs.moduleResultView
      if (moduleResultView && moduleResultView.checkedResults && moduleResultView.checkedResults.length > 0) {
        Events.$emit('openModalLeavingWithResults', callback)
      } else {
        /* eslint-disable node/no-callback-literal */
        if (callback) callback(true)
        /* eslint-enable node/no-callback-literal */
      }
    }
  },
  components: {
    'module-tabbar': ModuleTabbar,
    'module-aggregate-view': ModuleAggregateView,
    'module-result-view': ModuleResultView,
    'custom-select': CustomSelect,
    'module-result-detail': ModuleResultDetail,
    'search-bar': SearchBar
  },
  beforeRouteLeave (to, from, next) {
    this.confirmBeforeLeavingWithResults((confirmed) => {
      if (confirmed) {
        return next()
      } else {
        return next(false)
      }
    })
  },
  async mounted () {
    const _this = this
    if (this.activeTab === 'all' && this.modules.length === 1) {
      this.setActiveTab(this.modules[0].connection)
    }

    if (this.$route.query?.query) {
      await this.launchSearchFromUrl()
    }

    if (!this.$store.state.searchStore.lastSearchParams) {
      return this.$router.push({ name: 'home' })
    }

    Events.$on('newSearchStarted', _this.onNewSearch)
    Events.$on('beforeSearchStarted', _this.onBeforeSearchStarted)
  },
  beforeUnmount () {
    const _this = this
    Events.$off('newSearchStarted', _this.onNewSearch)
    Events.$off('beforeSearchStarted', _this.onBeforeSearchStarted)
  }
}
</script>

<style lang="scss" scoped>
  @import "../assets/css/_constants";

  $searchbar_height: 46px;

  .result-container {
    height: calc(100% - #{$header_height});

    .searchbar-top {
      display: flex;
      height: 46px;
      z-index: 3;
      padding: 0 1.25rem;
      background-color: white;

      .searchbar-container {
        flex-grow: 1;
        flex-shrink: 1;
      }
    }

    .tab-container {
      height: $search_tabbar_height;
      display: flex;
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.46);
      z-index: 2;

      &-singleline {
        overflow-x: auto;
        overflow-y: hidden;
      }

      .sort-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
        flex-grow: 0;
        flex-shrink: 0;

        & > div {
          flex-grow: 0;
          flex-shrink: 0;
        }
      }

      .listmode-container {
        padding-top: 2px;
        margin: 0 20px;
      }
    }

    .tabcontent-container {
      height: calc(100% - $search_tabbar_height - $searchbar_height);
    }
  }

  .paneltransition{
    &-enter-from, &-leave-to {
      right: -500px;
    }

    &-enter-active {
      transition: right 0.5s ease;
    }

    &-leave-active {
      transition: right 0.2s ease;
    }
  }

  .choice {
    &-container {
      display: flex;
      flex-direction: row;
    }

    &-option {
      padding: 6px 6px 2px 6px;
      border-top: 1px solid #ddd;
      border-bottom: 4px solid #ddd;
      cursor: pointer;
      white-space: nowrap;

      &:first-child {
        border-left: 1px solid #ddd;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }

      &:last-child {
        border-right: 1px solid #ddd;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }

      &-active {
        border-bottom-color: $main_light;
      }

      &:hover:not(.choice-option-active) {
        border-bottom-color: $grey_dark;
      }
    }
  }
</style>
